/**
 * Projects Index Page
 * 
 * Displays all projects from all countries in a list + map view.
 * This page shows a global view of all available projects.
 */

import React, { useState, useEffect } from 'react';
import { fetchAllProjects } from '../../services/apiClient';
import Navigation from '../../components/tulote/Navigation';
import ProjectsList from '../ProjectsListPage/ProjectsList';
import ProjectsMap from '../ProjectsListPage/ProjectsMap';
import { COUNTRY_CONFIG, DEFAULT_COUNTRY } from '../../config/countryConfig';

function ProjectsIndexPage() {
    const defaultCountryConfig = COUNTRY_CONFIG[DEFAULT_COUNTRY];

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projectsBbox, setProjectsBbox] = useState(null);

    // Hover/selection state for map
    const [hoveredProjectId, setHoveredProjectId] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(null);

    // Fetch all projects on mount
    useEffect(() => {
        const loadAllProjects = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await fetchAllProjects();
                
                // Ordenar proyectos: primero PUBLICADO, luego EDICION, y dentro de cada grupo por updated_at
                const sortedProjects = [...(response.data || [])].sort((a, b) => {
                    // Prioridad por status: PUBLICADO primero (0), EDICION después (1)
                    const statusPriority = {
                        'PUBLICADO': 0,
                        'EDICION': 1
                    };
                    const priorityA = statusPriority[a.status] ?? 2;
                    const priorityB = statusPriority[b.status] ?? 2;
                    
                    // Si tienen diferente prioridad, ordenar por prioridad
                    if (priorityA !== priorityB) {
                        return priorityA - priorityB;
                    }
                    
                    // Si tienen la misma prioridad, ordenar por updated_at (más recientes primero)
                    const dateA = a.updated_at ? new Date(a.updated_at) : new Date(0);
                    const dateB = b.updated_at ? new Date(b.updated_at) : new Date(0);
                    return dateB - dateA; // Orden descendente (más recientes primero)
                });
                
                setProjects(sortedProjects);
                setProjectsBbox(response.bbox);
            } catch (err) {
                console.error('Error loading all projects:', err);
                setError(err.message);
                setProjects([]);
                setProjectsBbox(null);
            } finally {
                setLoading(false);
            }
        };

        loadAllProjects();
    }, []);

    return (
        <div className="h-screen bg-gray-100 dark:bg-gray-900 flex flex-col overflow-hidden">
            {/* Navigation Header */}
            <Navigation />

            {/* Main Content: Sidebar + Map */}
            <div className="flex-1 flex relative overflow-hidden">
                {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-10">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500 mx-auto mb-4"></div>
                            <p className="text-gray-600 dark:text-gray-400">Cargando proyectos...</p>
                        </div>
                    </div>
                ) : error ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-10">
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md">
                            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
                                Error al cargar proyectos
                            </h3>
                            <p className="text-red-600 dark:text-red-300 mb-4">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Reintentar
                            </button>
                        </div>
                    </div>
                ) : projects.length > 0 ? (
                    <>
                        {/* Sidebar - Projects List (scrollable) */}
                        <div className="w-96 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col h-full overflow-hidden shadow-sm">
                            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gradient-to-r from-orange-50 to-white dark:from-gray-800 dark:to-gray-800">
                                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                    Todos los Proyectos
                                </h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {projects.length} {projects.length === 1 ? 'proyecto encontrado' : 'proyectos encontrados'} de todos los países
                                </p>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <div className="p-4">
                                    <ProjectsList
                                        projects={projects}
                                        hoveredProjectId={hoveredProjectId}
                                        selectedProjectId={selectedProjectId}
                                        onHover={setHoveredProjectId}
                                        onSelect={setSelectedProjectId}
                                        countryConfig={defaultCountryConfig}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Map - Takes remaining space (no scroll) */}
                        <div className="flex-1 relative overflow-hidden" style={{ height: '100%', minHeight: 0 }}>
                            <ProjectsMap
                                key={`map-all-${projects.length}`}
                                projects={projects}
                                hoveredProjectId={hoveredProjectId}
                                selectedProjectId={selectedProjectId}
                                onHover={setHoveredProjectId}
                                onSelect={setSelectedProjectId}
                                countryConfig={defaultCountryConfig}
                                countryCode="ALL"
                                projectsBbox={projectsBbox}
                            />
                        </div>
                    </>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-10">
                        <div className="text-center p-8">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                No hay proyectos disponibles
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                No se encontraron proyectos publicados.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProjectsIndexPage;

